import { apiClient } from '@/utils/api-client'
import { paginatedDirectoryParams } from '@/utils/paginated-directory-params'

const API_BASE = '/management-api-tokens'

// Crockford Base32 — 26 chars, no I/L/O/U (case-insensitive: API returns lowercase ULIDs)
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i

function assertUlid(value, name = 'id') {
    if (!ULID_RE.test(value)) {
        throw new Error(`Invalid ${name}: "${value}"`)
    }
}

class ManagementApiTokenService {
    /**
     * The account's active tokens. Paginated server-side — the response carries a
     * `meta` block ({ page, per_page, total, total_pages }) alongside `data`.
     *
     * @param {{page?: number, perPage?: number}} params
     */
    async list(params = {}) {
        return await apiClient
            .get(API_BASE, {
                params: {
                    page: params.page,
                    per_page: params.perPage,
                },
            })
            .then((response) => response.data)
    }

    async create(payload) {
        return await apiClient
            .post(API_BASE, payload)
            .then((response) => response.data)
    }

    async updateScopes(tokenUlid, payload) {
        assertUlid(tokenUlid, 'tokenUlid')
        return await apiClient
            .put(API_BASE + '/' + tokenUlid, payload)
            .then((response) => response.data)
    }

    async revoke(tokenUlid) {
        assertUlid(tokenUlid, 'tokenUlid')
        return await apiClient
            .delete(API_BASE + '/' + tokenUlid)
            .then((response) => response.data)
    }

    async getEffectivePermissions(tokenUlid) {
        assertUlid(tokenUlid, 'tokenUlid')
        return await apiClient
            .get(API_BASE + '/' + tokenUlid + '/effective-permissions')
            .then((response) => response.data)
    }

    /**
     * Account-wide token directory. Paginated server-side — the response carries a
     * `meta` block ({ page, per_page, total, total_pages }) alongside `data`.
     *
     * @param {{page?: number, perPage?: number, status?: 'active'|'revoked'}} params
     */
    async directory(params = {}) {
        return await apiClient
            .get(API_BASE + '/directory', {
                params: paginatedDirectoryParams(params),
            })
            .then((response) => response.data)
    }

    async rotate(tokenUlid, payload = {}) {
        assertUlid(tokenUlid, 'tokenUlid')
        return await apiClient
            .post(API_BASE + '/' + tokenUlid + '/rotate', payload)
            .then((response) => response.data)
    }

    async bulkRevoke(payload) {
        return await apiClient
            .post(API_BASE + '/bulk-revoke', payload)
            .then((response) => response.data)
    }

    async usageLog(tokenUlid) {
        assertUlid(tokenUlid, 'tokenUlid')
        return await apiClient
            .get(API_BASE + '/' + tokenUlid + '/usage-log')
            .then((response) => response.data)
    }
}

export default new ManagementApiTokenService()
